import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Toggle } from "@/components/Toggle/Toggle";

import { classNames } from "@/utils/classNames";

/** One number on a source card's metric strip. */
export interface SourceMetric {
	label: string;
	value: number;
	tone?: "neutral" | "ready" | "skipped";
}

interface TrackerSourceCardProps {
	icon: ReactNode;
	iconClassName: string;
	name: string;
	meta: string;
	statusLabel: string;
	statusTone: "tracking" | "live" | "paused";
	metrics: SourceMetric[];
	/** Shown when the source had to drop rows, with a way to get them back. */
	skippedNotice?: { text: string; onDownload: () => void };
	footerText: string;
	toggle: { label: string; isChecked: boolean; onChange: () => void };
	onRemove: () => void;
	removeLabel: string;
}

const METRIC_TONE = {
	neutral: "text-text",
	ready: "text-status-success",
	skipped: "text-status-warning",
};

const STATUS_TONE = {
	tracking: "bg-status-success-light text-status-success",
	live: "bg-blue-action-200 text-brand-light",
	paused: "bg-divider text-text-meta",
};

/**
 * One added source, with an honest account of what it produced (PREC-3417).
 *
 * The metric strip is the visible half of the ticket: a source that dropped a third of its
 * rows used to look identical to one that imported cleanly, so nobody ever fixed their file.
 */
export const TrackerSourceCard: FunctionComponent<TrackerSourceCardProps> = ({
	icon,
	iconClassName,
	name,
	meta,
	statusLabel,
	statusTone,
	metrics,
	skippedNotice,
	footerText,
	toggle,
	onRemove,
	removeLabel,
}) => {
	const { t } = useTranslation();

	return (
		<div className="mb-3 rounded-xl border border-border-light bg-white p-4">
			<div className="flex items-center gap-3">
				<div
					className={classNames(
						"flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[9px]",
						iconClassName
					)}
				>
					{icon}
				</div>
				<div className="min-w-0 flex-1">
					<div className="truncate text-ssm font-semibold">{name}</div>
					<div className="truncate text-xs text-text-meta">{meta}</div>
				</div>
				<span
					className={classNames(
						"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
						STATUS_TONE[statusTone]
					)}
				>
					{statusLabel}
				</span>
				<button
					aria-label={removeLabel}
					className="cursor-pointer rounded-md px-2 py-1 text-xs text-text-meta duration-200 hover:bg-brand-white hover:text-status-error"
					title={removeLabel}
					type="button"
					onClick={onRemove}
				>
					{t("basics.delete")}
				</button>
			</div>

			{metrics.length > 0 && (
				<div className="mt-3.5 flex overflow-hidden rounded-[9px] border border-border-light">
					{metrics.map((metric, index) => (
						<div
							key={metric.label}
							className={classNames(
								"flex-1 px-3 py-2.5 text-center",
								index < metrics.length - 1 ? "border-r border-border-light" : ""
							)}
						>
							<div
								className={classNames(
									"text-lg font-bold leading-tight",
									METRIC_TONE[metric.tone ?? "neutral"]
								)}
							>
								{metric.value}
							</div>
							<div className="mt-0.5 text-xs text-text-meta">{metric.label}</div>
						</div>
					))}
				</div>
			)}

			{skippedNotice && (
				<div className="mt-3 flex items-center gap-2 rounded-lg bg-status-warning-light px-3 py-2.5 text-xs text-brand-black">
					<Icon className="h-3.5 w-3.5 flex-shrink-0" icon={faTriangleExclamation} />
					<span>{skippedNotice.text}</span>
					<button
						className="ml-auto cursor-pointer whitespace-nowrap font-semibold underline"
						type="button"
						onClick={skippedNotice.onDownload}
					>
						{t("profileTracker.sources.added.downloadSkipped")}
					</button>
				</div>
			)}

			<div className="mt-3 flex items-center justify-between text-xs text-text-meta">
				<span>{footerText}</span>
				<div className="flex items-center gap-2">
					<span className="font-medium">{toggle.label}</span>
					<Toggle isChecked={toggle.isChecked} onChange={toggle.onChange} />
				</div>
			</div>
		</div>
	);
};
